import { c as _c } from "react/compiler-runtime";
import { FaroBulkDialog } from "@components/common/faro-table/bulk-dialog/bulk-dialog";
import { BulkSuccessDialog } from "@components/common/faro-table/bulk-dialog/bulk-success-dialog";
import { FaroTextMenuButton } from "@components/common/faro-text-menu-button";
import { getRoleMenuItemSx, RoleMenuItem } from "@components/role/role-menu-item";
import { useChangeRoleBulkDialogContent } from "@components/table/members/bulk-actions/bulk-change-role-helper";
import { FaroCoreApiConst } from "@stellar/core-api-client";
import { projectMembersSelector, selectedProjectManagerSelector } from "@store/projects/projects-selector";
import { updateMemberRoleInProjectBulk } from "@store/projects/projects-slice-thunk";
import { useAppDispatch, useAppSelector } from "@store/store-helper";
import { bulkActionNameSelector, selectedEntitiesSelector, shouldShowSuccessDialogSelector } from "@store/table/table-selector";
import { setBulkActionName, updateOne } from "@store/table/table-slice";
import { currentUserSelector } from "@store/user/user-selector";
import { isProjectAdminSelfDowngrade } from "@utils/access-control/project-role-change-guard";
import { BulkActionEvents } from "@utils/track-event/track-event-list";
import { useEffect, useState } from "react";
import { Text, UserRoleIcon, MenuItem } from "@flat-ui";
import { useBulkActionsMode } from "@hooks/use-bulk-actions-mode";
import { getBulkActionButtonConfig } from "@components/common/faro-table/bulk-actions/bulk-actions-config";

/** Button allowing to change role of members in project */
export function BulkChangeProjectRole(t0) {
  const $ = _c(36);
  const {
    companyId,
    projectId
  } = t0;
  const [shouldShowDialog, setShouldShowDialog] = useState(false);
  const [newRole, setNewRole] = useState("RESOURCE_VIEWER");
  const {
    user: selectedProjectManager
  } = useAppSelector(selectedProjectManagerSelector);
  const currentUser = useAppSelector(currentUserSelector);
  const projectMembers = useAppSelector(projectMembersSelector);
  const bulkActionName = useAppSelector(bulkActionNameSelector);
  const dispatch = useAppDispatch();
  const shouldShowSuccessDialog = useAppSelector(shouldShowSuccessDialogSelector);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = selectedEntitiesSelector("members");
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const selectedEntities = useAppSelector(t1);
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = selectedEntitiesSelector("teams");
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const selectedTeams = useAppSelector(t2);
  const mode = useBulkActionsMode();
  const selectedEntitiesWithoutTeams = selectedEntities.filter(_temp);
  let t3;
  if ($[2] !== newRole) {
    t3 = {
      subjectType: "project",
      newRole
    };
    $[2] = newRole;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const {
    bulkDialogContent,
    successDialogContent
  } = useChangeRoleBulkDialogContent(t3);
  let t4;
  let t5;
  if ($[4] !== shouldShowSuccessDialog) {
    t4 = () => {
      if (shouldShowSuccessDialog) {
        setShouldShowDialog(false);
      }
    };
    t5 = [shouldShowSuccessDialog];
    $[4] = shouldShowSuccessDialog;
    $[5] = t4;
    $[6] = t5;
  } else {
    t4 = $[5];
    t5 = $[6];
  }
  useEffect(t4, t5);
  let t6;
  if ($[7] !== currentUser || $[8] !== dispatch || $[9] !== projectMembers || $[10] !== selectedProjectManager) {
    t6 = function onNewRoleClicked(event) {
      dispatch(setBulkActionName("changeRole"));
      if (selectedProjectManager) {
        dispatch(updateOne({
          id: selectedProjectManager.identity,
          changes: {
            status: "not-allowed",
            message: "Cannot change the role of project owner"
          }
        }));
      }
      const currentUserProjectMember = projectMembers.find(member => member.identity === currentUser?.identity);
      if (currentUserProjectMember && isProjectAdminSelfDowngrade({
        currentUser,
        memberIdentity: currentUserProjectMember.identity,
        memberProjectRole: currentUserProjectMember.role
      })) {
        dispatch(updateOne({
          id: currentUserProjectMember.identity,
          changes: {
            status: "not-allowed",
            message: "You cannot change your own role as Project Admin"
          }
        }));
      }
      setNewRole(event.currentTarget.dataset.myValue);
      setShouldShowDialog(true);
    };
    $[7] = currentUser;
    $[8] = dispatch;
    $[9] = projectMembers;
    $[10] = selectedProjectManager;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  const onNewRoleClicked = t6;
  let t7;
  if ($[12] !== mode) {
    t7 = getBulkActionButtonConfig(mode, "Change role in project");
    $[12] = mode;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  const config = t7;
  let t8;
  if ($[14] !== config.shouldShowText) {
    t8 = config.shouldShowText ? <Text variant="buttonM">Change role</Text> : undefined;
    $[14] = config.shouldShowText;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  const t9 = config.tooltipText;
  let t10;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = {
      anchorOrigin: {
        vertical: "top",
        horizontal: "left"
      },
      transformOrigin: {
        vertical: "bottom",
        horizontal: "left"
      }
    };
    $[16] = t10;
  } else {
    t10 = $[16];
  }
  const t11 = config.rippleColor;
  let t12;
  if ($[17] !== config.sx) {
    t12 = () => config.sx;
    $[17] = config.sx;
    $[18] = t12;
  } else {
    t12 = $[18];
  }
  let t13;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = <UserRoleIcon sx={{
      width: "18px",
      height: "18px"
    }} />;
    $[19] = t13;
  } else {
    t13 = $[19];
  }
  let t14;
  if ($[20] !== onNewRoleClicked) {
    t14 = FaroCoreApiConst.allProjectRoles.map(role => role !== "RESOURCE_OWNER" && <MenuItem data-my-value={role} key={role} value={role} onClick={onNewRoleClicked} sx={getRoleMenuItemSx({
      isTableCell: false
    })}><RoleMenuItem role={role} /></MenuItem>);
    $[20] = onNewRoleClicked;
    $[21] = t14;
  } else {
    t14 = $[21];
  }
  let t15;
  if ($[22] !== config.rippleColor || $[23] !== config.tooltipText || $[24] !== t12 || $[25] !== t14 || $[26] !== t8) {
    t15 = <FaroTextMenuButton buttonTitle={t8} tooltipText={t9} menuPosition={t10} rippleColor={t11} shouldShowEndIcon={false} buttonSx={t12} startIcon={t13}>{t14}</FaroTextMenuButton>;
    $[22] = config.rippleColor;
    $[23] = config.tooltipText;
    $[24] = t12;
    $[25] = t14;
    $[26] = t8;
    $[27] = t15;
  } else {
    t15 = $[27];
  }
  let t16;
  if ($[28] !== bulkActionName || $[29] !== shouldShowSuccessDialog || $[30] !== successDialogContent) {
    t16 = bulkActionName === "changeRole" && shouldShowSuccessDialog && <BulkSuccessDialog dialogContent={successDialogContent} />;
    $[28] = bulkActionName;
    $[29] = shouldShowSuccessDialog;
    $[30] = successDialogContent;
    $[31] = t16;
  } else {
    t16 = $[31];
  }
  const t17 = bulkActionName === "changeRole" && shouldShowDialog && <FaroBulkDialog dialogContent={bulkDialogContent} onClose={() => setShouldShowDialog(false)} trackingEvent={BulkActionEvents.bulkChangeRole} bulkActionCallback={member_0 => dispatch(updateMemberRoleInProjectBulk({
    companyId,
    projectId,
    userIdentity: member_0.identity,
    payload: {
      role: newRole
    }
  })).unwrap()} selectedEntities={[...selectedTeams, ...selectedEntitiesWithoutTeams]} uniqueIdKey="identity" tableDataType="members" />;
  let t18;
  if ($[32] !== t15 || $[33] !== t16 || $[34] !== t17) {
    t18 = <>{t15}{t16}{t17}</>;
    $[32] = t15;
    $[33] = t16;
    $[34] = t17;
    $[35] = t18;
  } else {
    t18 = $[35];
  }
  return t18;
}
function _temp(entities) {
  return entities.kind === "user";
}